import { Module } from '../../types';
import { regexModule5QuestionBank } from '../questionBanks/regexModule5Questions';

export const regexModule5: Module = {
  id: 'module5',
  title: 'Real-World Applications and Performance',
  description:
    'Apply regex skills to solve production problems including log parsing, data validation, and performance optimization.',
  duration: '4.5 hours',
  content: `# Real-World Applications and Performance

## 🎯 Learning Objectives

By the end of this module, you will:
- Parse and extract data from log files and structured text
- Implement robust data validation for forms and inputs
- Process and transform text using regex substitutions
- Understand and optimize regex performance characteristics
- Handle edge cases and international text properly
- Build maintainable regex patterns for production use

## 📚 Introduction

In this final module, we'll apply everything you've learned to solve real-world problems. You'll learn to parse log files, validate data, process text at scale, and most importantly, write efficient and maintainable regex patterns for production environments.

## 📄 Log File Parsing

Log parsing is one of the most common regex applications. Different log formats require different parsing strategies.

### Web Server Logs (Apache Combined Format)

Apache logs follow a standard format that can be parsed with regex:

- IP address
- Remote logname  
- Remote user
- Timestamp in brackets
- HTTP request in quotes
- Status code
- Bytes sent
- Referer in quotes
- User agent in quotes

Example log line:
\`192.168.1.1 - - [25/Dec/2023:10:30:15 +0000] "GET /api/users HTTP/1.1" 200 1234 "https://example.com" "Mozilla/5.0"\`

\`\`\`python
import re
from datetime import datetime
from urllib.parse import unquote

def parse_apache_log(log_line):
    pattern = r'''
        ^(\\S+)                           # IP address
        \\s+(\\S+)                         # Remote logname
        \\s+(\\S+)                         # Remote user
        \\s+\\[([^\\]]+)\\]                  # Timestamp
        \\s+"(\\w+)\\s+([^\\s]+)\\s+([^"]+)"  # HTTP method, path, version
        \\s+(\\d+)                         # Status code
        \\s+(\\d+|-)                       # Bytes sent
        \\s+"([^"]*)"                     # Referer
        \\s+"([^"]*)"                     # User agent
    '''
    
    match = re.match(pattern, log_line, re.VERBOSE)
    if match:
        ip, logname, user, timestamp, method, path, version, status, bytes_sent, referer, user_agent = match.groups()
        
        # Parse timestamp
        try:
            dt = datetime.strptime(timestamp, '%d/%b/%Y:%H:%M:%S %z')
        except ValueError:
            dt = None
            
        # Clean up path
        clean_path = unquote(path)
        
        return {
            'ip': ip,
            'logname': logname if logname != '-' else None,
            'user': user if user != '-' else None,
            'timestamp': dt,
            'method': method,
            'path': clean_path,
            'version': version,
            'status': int(status),
            'bytes': int(bytes_sent) if bytes_sent != '-' else 0,
            'referer': referer if referer != '-' else None,
            'user_agent': user_agent
        }
    return None
\`\`\`

### Application Logs

Application logs often have custom formats that need specific patterns:

\`\`\`python
def parse_application_logs(log_text):
    patterns = {
        'django': r'^\\[([^\\]]+)\\] (\\w+): (.+)$',
        'rails': r'^(\\w+), \\[([^\\]]+) #(\\d+)\\] +(\\w+) -- : (.+)$',
        'nginx_error': r'^(\\d{4}/\\d{2}/\\d{2} \\d{2}:\\d{2}:\\d{2}) \\[(\\w+)\\] (\\d+)#(\\d+): (.+)$'
    }
    
    results = {}
    
    for line in log_text.strip().split('\\n'):
        line = line.strip()
        if not line:
            continue
            
        for log_type, pattern in patterns.items():
            match = re.match(pattern, line)
            if match:
                if log_type not in results:
                    results[log_type] = []
                    
                if log_type == 'django':
                    timestamp, level, message = match.groups()
                    results[log_type].append({
                        'timestamp': timestamp,
                        'level': level,
                        'message': message
                    })
                break
    
    return results
\`\`\`

## ✅ Data Validation and Sanitization

Real-world validation requires handling edge cases and international input.

### Email Validation Strategy

Instead of trying to create one perfect email regex, use a layered approach:

\`\`\`python
import re

class EmailValidator:
    def __init__(self):
        # Basic format pattern
        self.basic_pattern = r'^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
        
        # More comprehensive pattern
        self.comprehensive_pattern = r'''
            ^(?=.{1,254}$)                      # Total length limit
            (?=.{1,64}@)                        # Local part length limit
            [a-zA-Z0-9!#$%&'*+/=?^_\`{|}~-]+     # Local part chars
            (?:\\.[a-zA-Z0-9!#$%&'*+/=?^_\`{|}~-]+)*  # Additional local parts
            @                                   # @ symbol
            (?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+ # Domain parts
            [a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$       # Final domain part
        '''
    
    def validate(self, email, level='basic'):
        if level == 'basic':
            return self._basic_validation(email)
        elif level == 'comprehensive':
            return self._comprehensive_validation(email)
        else:
            return self._layered_validation(email)
    
    def _basic_validation(self, email):
        return bool(re.match(self.basic_pattern, email))
    
    def _comprehensive_validation(self, email):
        return bool(re.match(self.comprehensive_pattern, email, re.VERBOSE))
    
    def _layered_validation(self, email):
        # Layer 1: Basic format
        if not re.match(self.basic_pattern, email):
            return False, "Invalid email format"
        
        # Layer 2: Length checks
        if len(email) > 254:
            return False, "Email too long"
        
        local, domain = email.rsplit('@', 1)
        if len(local) > 64:
            return False, "Local part too long"
        
        # Layer 3: Domain validation
        if '..' in domain:
            return False, "Consecutive dots in domain"
        
        if domain.startswith('.') or domain.endswith('.'):
            return False, "Domain cannot start or end with dot"
        
        return True, "Valid email"
\`\`\`

### Phone Number Processing

Phone numbers come in many formats. Here's a robust processing system:

\`\`\`python
import re

class PhoneNumberProcessor:
    def __init__(self):
        # Patterns for different regions
        self.patterns = {
            'us': r'^(?:\\+?1[-\\.\\s]?)?\\(?([0-9]{3})\\)?[-\\.\\s]?([0-9]{3})[-\\.\\s]?([0-9]{4})$',
            'uk': r'^(?:\\+?44[-\\.\\s]?)?(?:0[-\\.\\s]?)?([0-9]{4})[-\\.\\s]?([0-9]{6})$',
            'international': r'^\\+([1-9]\\d{0,3})[-\\.\\s]?(.+)$'
        }
    
    def extract_digits(self, phone):
        """Extract only digits from phone number"""
        return re.sub(r'[^\\d]', '', phone)
    
    def normalize_us_phone(self, phone):
        """Normalize US phone numbers to (XXX) XXX-XXXX format"""
        digits = self.extract_digits(phone)
        
        if len(digits) == 10:
            return f"({digits[:3]}) {digits[3:6]}-{digits[6:]}"
        elif len(digits) == 11 and digits[0] == '1':
            return f"({digits[1:4]}) {digits[4:7]}-{digits[7:]}"
        else:
            return None
    
    def parse_phone(self, phone):
        """Parse phone number and return components"""
        # Clean input
        cleaned = re.sub(r'[^\\d+\\-\\.\\s()]', '', phone)
        
        # Try US format first
        us_match = re.match(self.patterns['us'], cleaned)
        if us_match:
            area_code, exchange, number = us_match.groups()
            return {
                'country': 'US',
                'area_code': area_code,
                'exchange': exchange,
                'number': number,
                'formatted': f"({area_code}) {exchange}-{number}",
                'international': f"+1 {area_code} {exchange} {number}"
            }
        
        return None
\`\`\`

### Data Anonymization

Replace sensitive data with placeholders:

\`\`\`python
import re
import hashlib

class DataAnonymizer:
    def __init__(self):
        self.patterns = {
            'email': r'\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}\\b',
            'phone': r'\\b(?:\\+?1[-\\.\\s]?)?\\(?[0-9]{3}\\)?[-\\.\\s]?[0-9]{3}[-\\.\\s]?[0-9]{4}\\b',
            'ssn': r'\\b\\d{3}-\\d{2}-\\d{4}\\b',
            'credit_card': r'\\b(?:\\d{4}[-\\s]?){3}\\d{4}\\b',
            'ip_address': r'\\b(?:[0-9]{1,3}\\.){3}[0-9]{1,3}\\b',
            'url': r'https?://[^\\s<>"]+',
            'date': r'\\b\\d{1,2}[/-]\\d{1,2}[/-]\\d{2,4}\\b'
        }
        
        self.replacements = {
            'email': '[EMAIL]',
            'phone': '[PHONE]',
            'ssn': '[SSN]',
            'credit_card': '[CARD]',
            'ip_address': '[IP]',
            'url': '[URL]',
            'date': '[DATE]'
        }
    
    def anonymize_text(self, text, preserve_structure=False):
        """Anonymize sensitive data in text"""
        result = text
        found_items = {}
        
        for data_type, pattern in self.patterns.items():
            matches = re.findall(pattern, text)
            if matches:
                found_items[data_type] = matches
                
                if preserve_structure:
                    # Generate consistent hashes for same values
                    for match in set(matches):
                        hash_suffix = hashlib.md5(match.encode()).hexdigest()[:6]
                        replacement = f"[{data_type.upper()}_{hash_suffix}]"
                        result = result.replace(match, replacement)
                else:
                    # Simple replacement
                    result = re.sub(pattern, self.replacements[data_type], result)
        
        return result, found_items
\`\`\`

## ⚡ Performance Optimization

Understanding regex performance is crucial for production applications.

### Common Performance Problems

\`\`\`python
import re
import time

def benchmark_pattern(pattern, text, iterations=10000):
    """Benchmark a regex pattern"""
    start = time.time()
    for _ in range(iterations):
        re.search(pattern, text)
    return time.time() - start

# Problem 1: Catastrophic Backtracking
print("1. Catastrophic Backtracking:")
problematic = "a" * 20 + "b"

# Bad pattern - causes exponential backtracking
bad_pattern = r"(a+)+c"
# Good pattern - linear performance
good_pattern = r"a+c"

print("Bad patterns can cause exponential backtracking!")
print("Good patterns have linear performance.")

# Problem 2: Unanchored patterns
print("\\n2. Unanchored vs Anchored:")
long_text = "word " * 10000 + "target"

unanchored = r"target"
anchored = r"target$"

print("Anchored patterns are more efficient when you know the position")
\`\`\`

### Optimization Techniques

\`\`\`python
import re
from functools import lru_cache

class OptimizedRegexProcessor:
    def __init__(self):
        self._compiled_patterns = {}
        self.stats = {'hits': 0, 'misses': 0, 'compile_time': 0}
    
    def get_compiled_pattern(self, pattern, flags=0):
        """Cache compiled patterns for reuse"""
        key = (pattern, flags)
        if key in self._compiled_patterns:
            self.stats['hits'] += 1
            return self._compiled_patterns[key]
        else:
            self.stats['misses'] += 1
            compiled = re.compile(pattern, flags)
            self._compiled_patterns[key] = compiled
            return compiled
    
    def efficient_multi_pattern_search(self, text, patterns):
        """Efficiently search for multiple patterns"""
        # Combine patterns into one for single pass
        combined_pattern = '|'.join(f'(?P<pattern_{i}>{pattern})' 
                                   for i, pattern in enumerate(patterns))
        compiled = self.get_compiled_pattern(combined_pattern)
        
        results = []
        for match in compiled.finditer(text):
            for i, pattern in enumerate(patterns):
                group_name = f'pattern_{i}'
                if match.group(group_name):
                    results.append({
                        'pattern_index': i,
                        'pattern': pattern,
                        'match': match.group(group_name),
                        'start': match.start(),
                        'end': match.end()
                    })
                    break
        
        return results
    
    def optimized_email_extraction(self, text):
        """Optimized email extraction with pre-filtering"""
        # Quick pre-filter to avoid expensive regex on irrelevant text
        if '@' not in text:
            return []
        
        # Use compiled pattern
        pattern = r'\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}\\b'
        compiled = self.get_compiled_pattern(pattern, re.IGNORECASE)
        
        return compiled.findall(text)
\`\`\`

## 🌍 International Text and Unicode

When processing international text, consider:

\`\`\`python
import re
import unicodedata

class InternationalTextProcessor:
    def __init__(self):
        # Unicode category patterns
        self.patterns = {
            'word_chars': r'[\\w\\u00C0-\\u017F\\u0400-\\u04FF\\u4e00-\\u9fff]',
            'latin_script': r'[A-Za-z\\u00C0-\\u017F]',
            'cyrillic_script': r'[\\u0400-\\u04FF]',
            'cjk_script': r'[\\u4e00-\\u9fff]',
            'arabic_script': r'[\\u0600-\\u06FF]'
        }
    
    def normalize_text(self, text, form='NFD'):
        """Normalize Unicode text"""
        return unicodedata.normalize(form, text)
    
    def extract_words_international(self, text):
        """Extract words from international text"""
        # Normalize first
        normalized = self.normalize_text(text)
        
        # Use Unicode-aware word pattern
        pattern = self.patterns['word_chars'] + '+'
        words = re.findall(pattern, normalized)
        
        return words
    
    def detect_scripts(self, text):
        """Detect which scripts are present in text"""
        scripts = {}
        
        for script_name, pattern in self.patterns.items():
            if script_name.endswith('_script'):
                matches = re.findall(pattern, text)
                if matches:
                    scripts[script_name] = len(matches)
        
        return scripts
    
    def extract_emails_international(self, text):
        """Extract emails with international domain names"""
        # Support internationalized domain names
        pattern = r'\\b[\\w._%+-]+@[\\w.-]*[a-zA-Z\\u00a1-\\uffff][\\w.-]*\\.[a-zA-Z\\u00a1-\\uffff]{2,}\\b'
        return re.findall(pattern, text, re.UNICODE)
\`\`\`

## 🏆 Production Best Practices

### 1. Maintainable Patterns

Build patterns from documented components:

\`\`\`python
class EmailPatternBuilder:
    """Build email validation patterns from components"""
    
    def __init__(self):
        self.components = {
            'local_basic': r'[a-zA-Z0-9._%+-]+',
            'local_quoted': r'"[^"\\\\]*(?:\\\\.[^"\\\\]*)*"',
            'domain_basic': r'[a-zA-Z0-9.-]+',
            'domain_ip': r'\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\]',
            'tld': r'[a-zA-Z]{2,}'
        }
    
    def build_basic_pattern(self):
        """Build basic email pattern"""
        local = self.components['local_basic']
        domain = self.components['domain_basic']
        tld = self.components['tld']
        return f"^{local}@{domain}\\\\.{tld}$"
    
    def build_comprehensive_pattern(self):
        """Build comprehensive email pattern"""
        local = f"(?:{self.components['local_basic']}|{self.components['local_quoted']})"
        domain = f"(?:{self.components['domain_basic']}|{self.components['domain_ip']})"
        tld = self.components['tld']
        return f"^{local}@{domain}(?:\\\\.{tld})?$"
\`\`\`

### 2. Testing Strategy

\`\`\`python
import unittest
import re

class RegexTestSuite:
    """Comprehensive testing for regex patterns"""
    
    def __init__(self, pattern, description=""):
        self.pattern = pattern
        self.description = description
        self.compiled = re.compile(pattern)
    
    def test_matches(self, test_cases):
        """Test cases that should match"""
        results = []
        for test_input, expected_groups in test_cases:
            match = self.compiled.search(test_input)
            if match:
                actual_groups = match.groups()
                success = actual_groups == expected_groups if expected_groups else True
                results.append({
                    'input': test_input,
                    'expected': expected_groups,
                    'actual': actual_groups,
                    'success': success
                })
            else:
                results.append({
                    'input': test_input,
                    'expected': expected_groups,
                    'actual': None,
                    'success': False
                })
        return results
    
    def test_non_matches(self, test_cases):
        """Test cases that should NOT match"""
        results = []
        for test_input in test_cases:
            match = self.compiled.search(test_input)
            success = match is None
            results.append({
                'input': test_input,
                'matched': match.group() if match else None,
                'success': success
            })
        return results
\`\`\`

## 📊 Key Takeaways

1. **Layer your validation** - Don't rely on regex alone
2. **Performance matters** - Profile and optimize critical patterns
3. **Test thoroughly** - Use real-world data and edge cases
4. **Document everything** - Make patterns maintainable
5. **Consider internationalization** - Handle Unicode properly
6. **Have fallback strategies** - Don't let regex failures break your app

## 🚀 Final Project Ideas

1. **Log Analyzer**: Parse web server logs and generate reports
2. **Data Cleaner**: Sanitize CSV files with various data formats
3. **Text Processor**: Extract and normalize contact information from documents
4. **Validation Library**: Create reusable validation patterns for common data types
5. **Performance Benchmarker**: Compare different regex approaches for speed

## 🎉 Congratulations!

You've mastered regular expressions from basic patterns to production-ready applications. You now have the skills to tackle complex text processing challenges with confidence and efficiency.

### Your Regex Journey

- **Module 1**: Learned the fundamentals of pattern matching
- **Module 2**: Mastered character classes and quantifiers
- **Module 3**: Gained precision with anchors and capturing groups
- **Module 4**: Explored advanced patterns and lookarounds
- **Module 5**: Applied knowledge to real-world scenarios

### Next Steps

- Practice with real-world datasets
- Contribute to open-source projects that use regex
- Explore regex in other programming languages
- Learn about finite automata and regex theory
- Build your own text processing tools

Remember: Regex is a powerful tool, but like any tool, it should be used appropriately. Sometimes a simple string method is better than a complex regex. Always consider readability, maintainability, and performance in your solutions.

Happy pattern matching! 🎯`,

  exercises: [
    {
      id: 'module5-exercise1',
      title: 'Apache Log Parsing',
      description: 'Create a pattern to extract IP address from Apache log format: ^(\\\\S+)',
      type: 'regex',
      difficulty: 'easy',
      pattern: '^(\\S+)',
      testCases: [
        {
          input: '192.168.1.1 - - [25/Dec/2023:10:30:15 +0000]',
          expected: true,
          description: 'Standard Apache log line',
        },
        {
          input: '10.0.0.1 john admin [25/Dec/2023:10:31:20 +0000]',
          expected: true,
          description: 'Log with user info',
        },
        {
          input: '127.0.0.1 - - [01/Jan/2024:00:00:00 +0000]',
          expected: true,
          description: 'Localhost IP',
        },
        { input: ' 192.168.1.1', expected: false, description: 'IP not at start' },
        { input: '', expected: false, description: 'Empty string' },
        { input: 'not-an-ip', expected: true, description: 'Any non-space chars at start' },
      ],
      hints: [
        'Use ^ to match from the beginning of the line',
        'Use \\S+ to match one or more non-space characters',
        'Use parentheses () to capture the IP address',
      ],
    },
    {
      id: 'module5-exercise2',
      title: 'Email Extraction',
      description:
        'Create a pattern to extract emails: \\\\b[\\\\w._%+-]+@[\\\\w.-]+\\\\.[a-zA-Z]{2,}\\\\b',
      type: 'regex',
      difficulty: 'medium',
      pattern: '\\b[\\w._%+-]+@[\\w.-]+\\.[a-zA-Z]{2,}\\b',
      testCases: [
        {
          input: 'Contact support@company.com for help',
          expected: true,
          description: 'Email in text',
        },
        {
          input: 'user.name+tag@domain.co.uk',
          expected: true,
          description: 'Complex email format',
        },
        {
          input: 'admin@test-server.org',
          expected: true,
          description: 'Email with hyphen in domain',
        },
        { input: 'invalid@', expected: false, description: 'Incomplete email' },
        { input: '@domain.com', expected: false, description: 'Missing local part' },
        { input: 'user@domain', expected: false, description: 'Missing TLD' },
      ],
      hints: [
        'Use \\b for word boundaries to match complete emails',
        'Use [\\w._%+-] for valid email characters in local part',
        'Use [\\w.-] for valid domain characters',
        'Use \\. to match literal dot before TLD',
        'Use [a-zA-Z]{2,} for top-level domain (2+ letters)',
      ],
    },
    {
      id: 'module5-exercise3',
      title: 'Phone Number Normalization',
      description:
        'Create a pattern to match US phone numbers: ^\\\\(?([0-9]{3})\\\\)?[-\\.\\\\s]?([0-9]{3})[-\\.\\\\s]?([0-9]{4})$',
      type: 'regex',
      difficulty: 'hard',
      pattern: '^\\(?([0-9]{3})\\)?[-\\.\\s]?([0-9]{3})[-\\.\\s]?([0-9]{4})$',
      testCases: [
        {
          input: '(555) 123-4567',
          expected: true,
          description: 'Standard format with parentheses',
        },
        { input: '555-123-4567', expected: true, description: 'Dash separated format' },
        { input: '555.123.4567', expected: true, description: 'Dot separated format' },
        { input: '5551234567', expected: true, description: 'No separators' },
        { input: '555 123 4567', expected: true, description: 'Space separated' },
        { input: '1-555-123-4567', expected: false, description: 'With country code' },
        { input: '555-12-4567', expected: false, description: 'Wrong format' },
      ],
      hints: [
        'Use ^ and $ to match the entire string',
        'Use \\(? and \\)? for optional parentheses around area code',
        'Use ([0-9]{3}) to capture area code (exactly 3 digits)',
        'Use [-\\.\\s]? for optional separators (dash, dot, or space)',
        'Use ([0-9]{3}) and ([0-9]{4}) to capture exchange and number',
      ],
    },
    {
      id: 'module5-exercise4',
      title: 'Data Anonymization',
      description:
        'Create a pattern to match Social Security Numbers: \\\\b\\\\d{3}-\\\\d{2}-\\\\d{4}\\\\b',
      type: 'regex',
      difficulty: 'medium',
      pattern: '\\b\\d{3}-\\d{2}-\\d{4}\\b',
      testCases: [
        { input: 'SSN: 123-45-6789', expected: true, description: 'Standard SSN format' },
        {
          input: 'My SSN is 987-65-4321 for records',
          expected: true,
          description: 'SSN in sentence',
        },
        { input: '555-12-3456', expected: true, description: 'Valid SSN format' },
        { input: '12-345-6789', expected: false, description: 'Wrong digit grouping' },
        { input: '123456789', expected: false, description: 'No hyphens' },
        {
          input: 'ID: 123-45-67890',
          expected: false,
          description: 'Too many digits in last group',
        },
      ],
      hints: [
        'Use \\b for word boundaries to match complete SSN',
        'Use \\d{3} for exactly 3 digits',
        'Use - for literal hyphen',
        'Use \\d{2} for exactly 2 digits',
        'Use \\d{4} for exactly 4 digits',
        'Pattern: 3 digits, hyphen, 2 digits, hyphen, 4 digits',
      ],
    },
    {
      id: 'module5-exercise5',
      title: 'URL Extraction',
      description: 'Create a pattern to match HTTP/HTTPS URLs: https?://[^\\\\s<>"]+',
      type: 'regex',
      difficulty: 'hard',
      pattern: 'https?://[^\\s<>"]+',
      testCases: [
        { input: 'Visit https://example.com', expected: true, description: 'HTTPS URL' },
        {
          input: 'Check out http://test.org/page',
          expected: true,
          description: 'HTTP URL with path',
        },
        {
          input: 'Go to https://domain.com/path?param=value',
          expected: true,
          description: 'URL with query parameters',
        },
        {
          input: 'Link: https://sub.domain.co.uk/long/path',
          expected: true,
          description: 'Complex URL',
        },
        {
          input: 'ftp://files.example.com',
          expected: false,
          description: 'FTP protocol (not HTTP/HTTPS)',
        },
        { input: 'https://', expected: false, description: 'Incomplete URL' },
        {
          input: 'Visit https://example.com and click',
          expected: true,
          description: 'URL in middle of text',
        },
      ],
      hints: [
        'Use https? to match "http" or "https" (s is optional)',
        'Use :// for the protocol separator',
        'Use [^\\s<>"]+ to match any characters except spaces and HTML/quote chars',
        'This ensures URLs stop at whitespace or HTML boundaries',
        'The + ensures at least one character after the protocol',
      ],
    },
    {
      id: 'module5-exercise6',
      title: 'Log Level Extraction',
      description: 'Create a pattern to extract log levels: \\\\[(ERROR|WARN|INFO|DEBUG)\\\\]',
      type: 'regex',
      difficulty: 'medium',
      pattern: '\\[(ERROR|WARN|INFO|DEBUG)\\]',
      testCases: [
        {
          input: '[ERROR] Database connection failed',
          expected: true,
          description: 'Error log entry',
        },
        { input: '[INFO] User login successful', expected: true, description: 'Info log entry' },
        { input: '[WARN] Memory usage high', expected: true, description: 'Warning log entry' },
        {
          input: '[DEBUG] Processing request ID 12345',
          expected: true,
          description: 'Debug log entry',
        },
        {
          input: '[TRACE] Detailed execution info',
          expected: false,
          description: 'Unsupported log level',
        },
        {
          input: 'ERROR: Something went wrong',
          expected: false,
          description: 'Log level without brackets',
        },
        {
          input: 'System [INFO] started successfully',
          expected: true,
          description: 'Log level in middle',
        },
      ],
      hints: [
        'Use \\[ and \\] to match literal square brackets',
        'Use (ERROR|WARN|INFO|DEBUG) for alternation of log levels',
        'Use parentheses to capture the log level',
        'The pattern matches bracketed log levels exactly',
        'Alternation checks each option from left to right',
      ],
    },
  ],

  quiz: {
    id: 'module5-quiz',
    title: 'Real-World Applications and Performance Quiz',
    questions: [
      {
        id: 'q1',
        question:
          'What is the main advantage of using layered validation instead of relying solely on regex?',
        choices: [
          'Layered validation is faster than regex',
          'It provides better error messages and handles edge cases',
          'Regex cannot validate email addresses properly',
          'Layered validation uses less memory',
        ],
        correctAnswer: 1,
        explanation:
          'Layered validation combines regex with additional checks (length limits, character validation, domain rules) to provide comprehensive validation with specific error messages and better edge case handling.',
      },
      {
        id: 'q2',
        question: 'What is catastrophic backtracking in regex?',
        choices: [
          'When regex patterns match too many results',
          'When nested quantifiers cause exponential performance degradation',
          'When regex compilation fails',
          'When patterns are too complex to understand',
        ],
        correctAnswer: 1,
        explanation:
          'Catastrophic backtracking occurs when nested quantifiers like (a+)+ create exponential time complexity as the regex engine tries every possible combination when the pattern fails to match.',
      },
      {
        id: 'q3',
        question: 'Why should you compile and cache regex patterns in production?',
        choices: [
          'To save memory usage',
          'To avoid recompiling the same patterns repeatedly',
          'To make patterns case-insensitive',
          'To enable international character support',
        ],
        correctAnswer: 1,
        explanation:
          'Compiling regex patterns is expensive. Caching compiled patterns avoids the overhead of recompiling the same pattern multiple times, significantly improving performance in production applications.',
      },
      {
        id: 'q4',
        question: 'When processing international text, what should you consider?',
        choices: [
          'Only ASCII characters are important',
          'Unicode normalization and script-specific patterns',
          'International text cannot be processed with regex',
          'All languages use the same character patterns',
        ],
        correctAnswer: 1,
        explanation:
          'International text requires Unicode normalization (NFD/NFC) and script-specific character ranges. Different writing systems have different patterns, and proper Unicode handling is essential.',
      },
      {
        id: 'q5',
        question: 'What is the best practice for data anonymization with regex?',
        choices: [
          'Delete all sensitive data permanently',
          'Replace sensitive patterns with consistent placeholders',
          'Encrypt the entire text file',
          'Only remove email addresses',
        ],
        correctAnswer: 1,
        explanation:
          'Data anonymization should replace sensitive data (emails, phones, SSNs) with consistent placeholders while preserving text structure. This allows analysis while protecting privacy.',
      },
      {
        id: 'q6',
        question:
          'Why is it important to use word boundaries (\\\\b) in production regex patterns?',
        choices: [
          'They make patterns case-insensitive',
          'They prevent partial matches within larger words',
          'They improve regex performance',
          'They enable Unicode support',
        ],
        correctAnswer: 1,
        explanation:
          'Word boundaries prevent false positives by ensuring patterns match complete words rather than substrings. For example, searching for "cat" without boundaries would match "catalog" and "concatenate".',
      },
      {
        id: 'q7',
        question: 'What makes a regex pattern maintainable in production code?',
        choices: [
          'Using the shortest possible pattern',
          'Clear documentation, modular components, and comprehensive tests',
          'Avoiding all special characters',
          'Using only basic literal matching',
        ],
        correctAnswer: 1,
        explanation:
          'Maintainable regex requires documentation explaining what the pattern does, modular components that can be combined, comprehensive test cases covering edge cases, and clear naming conventions.',
      },
    ],
    questionBank: regexModule5QuestionBank,
    questionsPerQuiz: 10,
    passingScore: 7,
  },

  resources: [
    {
      id: 'regex-performance',
      title: 'Regex Performance Best Practices',
      type: 'document',
      url: '/resources/regex-performance.pdf',
      description: 'Guide to optimizing regex patterns for production use',
    },
    {
      id: 'log-parsing-guide',
      title: 'Log Parsing with Regex',
      type: 'document',
      url: '/resources/log-parsing-guide.pdf',
      description: 'Comprehensive guide to parsing various log formats',
    },
    {
      id: 'unicode-regex',
      title: 'International Text Processing',
      type: 'link',
      url: 'https://www.regular-expressions.info/unicode.html',
      description: 'Guide to handling Unicode and international text in regex',
    },
    {
      id: 'regex-security',
      title: 'Regex Security Considerations',
      type: 'link',
      url: 'https://owasp.org/www-community/attacks/Regular_expression_Denial_of_Service_-_ReDoS',
      description: 'OWASP guide to preventing ReDoS attacks',
    },
    {
      id: 'testing-regex',
      title: 'Testing Regex Patterns',
      type: 'document',
      url: '/resources/testing-regex.pdf',
      description: 'Strategies for comprehensive regex pattern testing',
    },
  ],
};
